import { BRAND, t } from '@procumi/shared';
import { useEffect, useRef, useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import { api, errMsg } from '../lib/api';
import { Dialog } from './Dialog';
import { isBuild } from '../lib/terms';

/** „procumi-comparatie-oferte (2).xlsx” → „Comparație oferte”: un nume de pornire, nu unul bun. */
const titleFromFile = (name: string) => {
  const base = name
    .replace(/\.xlsx$/i, '')
    .replace(/\(\d+\)/g, '')
    .replace(/[-_]+/g, ' ')
    .trim();
  return /procumi comparatie oferte|procumi offer comparison/i.test(base) ? '' : base.slice(0, 200);
};

/**
 * Șablonul „Comparație oferte” de pe site, adus în aplicație: pozițiile devin
 * o cerere, iar prețurile din șablon, ofertele furnizorilor. Omul alege doar
 * șantierul, numele și categoria; restul vine din fișier.
 */
export function TemplateImportDialog({
  onDone,
  onClose,
}: {
  onDone: (rfqId: string) => void;
  onClose: () => void;
}) {
  const sites = useAsync(() => api.sites(), [], 'sites');
  const cats = useAsync(() => api.categories(), [], 'categories');
  const [file, setFile] = useState<File | null>(null);
  const [siteId, setSiteId] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ rfqId: string; skipped: string[] } | null>(null);
  const input = useRef<HTMLInputElement>(null);

  const siteList = sites.data ?? [];
  const catList = (cats.data?.used.length ? cats.data.used : cats.data?.suggested) ?? [];

  // Un singur șantier deschis: îl alegem noi.
  useEffect(() => {
    if (!siteId && siteList.length === 1) setSiteId(siteList[0]!.id);
  }, [siteList, siteId]);

  const save = async () => {
    if (!file) return setError(t('Alege fișierul Excel cu șablonul completat.'));
    if (!siteId) return setError(t('Alege șantierul.'));
    if (!title.trim()) return setError(t('Dă un nume cererii.'));
    if (!category.trim()) return setError(t('Alege o categorie.'));
    setBusy(true);
    setError('');
    try {
      const r = await api.importTemplate({ file, siteId, title: title.trim(), category: category.trim() });
      if (r.skipped.length) setDone({ rfqId: r.rfq.id, skipped: r.skipped });
      else onDone(r.rfq.id);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Dialog onClose={() => onDone(done.rfqId)}>
        <div className="dlg-h">
          <div>
            <h2>{t('Cererea e gata, cu ofertele din șablon')}</h2>
            <p className="hint">{t('Câteva rânduri n-au intrat; le poți adăuga de mână în cerere.')}</p>
          </div>
        </div>
        <div className="dlg-b">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {done.skipped.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
        <div className="dlg-f">
          <button className="btn primary" type="button" onClick={() => onDone(done.rfqId)}>
            {t('Deschide cererea')}
          </button>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog onClose={onClose} dirty={Boolean(file)}>
      <div className="dlg-h">
        <div>
          <h2>{t('Din șablonul Excel')}</h2>
          <p className="hint">
            {t('Ai completat șablonul „Comparație oferte” de pe')}
            <a href={BRAND.site} target="_blank" rel="noopener">
              {t('procumi.com')}
            </a>
            {t(
              '? Încarcă-l aici: pozițiile devin o cerere de ofertă, iar prețurile, ofertele furnizorilor, cu tabelul comparativ gata.',
            )}
          </p>
        </div>
      </div>
      <div className="dlg-b">
        <div className="row">
          <div className="f" style={{ gridColumn: '1/-1' }}>
            {t('Fișierul')}
            <div className="imp-row" style={{ marginTop: 0 }}>
              <button className="btn" type="button" onClick={() => input.current?.click()}>
                {file ? t('Alege alt fișier') : t('Alege fișierul .xlsx')}
              </button>
              <span className="hint" style={{ margin: 0 }}>
                {file ? file.name : t('Șablonul completat, salvat ca .xlsx')}
              </span>
            </div>
            <input
              ref={input}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              hidden
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                setError('');
                if (f && !title.trim()) setTitle(titleFromFile(f.name));
                e.target.value = '';
              }}
            />
          </div>
          <label className="f" style={{ gridColumn: '1/-1' }} htmlFor="tpl-title">
            {t('Denumire cerere')}
            <input
              id="tpl-title"
              className="inp"
              value={title}
              placeholder={
                isBuild()
                  ? t('ex. Armătură fundații – Bloc P+4')
                  : t('ex. Aprovizionare noiembrie – sediul central')
              }
              onChange={(e) => setTitle(e.target.value)}
            />
          </label>
          <label className="f" htmlFor="tpl-site">
            {t('Șantier')}
            <select id="tpl-site" className="inp" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
              <option value="">{t('Alege șantierul…')}</option>
              {siteList.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.locality ? `${s.name} — ${s.locality}` : s.name}
                </option>
              ))}
            </select>
            {siteList.length === 0 && !sites.loading ? (
              <span className="hint" style={{ margin: 0 }}>
                {t('Nu ai niciun șantier deschis. Adaugă unul din secțiunea Șantiere.')}
              </span>
            ) : null}
          </label>
          <label className="f" htmlFor="tpl-cat">
            {t('Categorie')}
            <input
              id="tpl-cat"
              className="inp"
              list="tpl-cats"
              value={category}
              placeholder={t('ex. Armătură')}
              onChange={(e) => setCategory(e.target.value)}
            />
            <datalist id="tpl-cats">
              {catList.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </label>
        </div>
        <p className="hint" style={{ marginBottom: 0 }}>
          {t(
            'Furnizorii din capul coloanelor se caută în agendă după nume; cei care nu sunt acolo se adaugă, fără persoană de contact. Nu pleacă niciun mail.',
          )}
        </p>
        {error ? (
          <p className="read-warn" role="alert">
            {error}
          </p>
        ) : null}
      </div>
      <div className="dlg-f">
        <button className="btn" type="button" onClick={onClose}>
          {t('Renunță')}
        </button>
        <button className="btn primary" type="button" disabled={busy} onClick={() => void save()}>
          {busy ? t('Se citește…') : t('Fă cererea')}
        </button>
      </div>
    </Dialog>
  );
}
